/**
 * This is an extension to Leaflet allowing to display TomTom base map layers. It is based on the raster L.TileLayer
 * implementation. The options that can be passed on creation of this object are described for the .tomTomLayer()
 * function.
 *
 * @example
 *  var tomtomLayer = L.tomTomLayer({key: "<Your API key>"});
 *
 * @class TomTomLayer
 * @extends L.TileLayer
 */
/**
 * Instantiates a new instance of the TomTom base map layer.
 *
 * @method tomTomLayer
 * @param {Object} [options] All the options that Leaflet allows, as described here:
 *     http://leafletjs.com/reference.html#tilelayer-options Additional options, which are TomTom specific, are
 *     described below.
 * @param {String} [options.key] This is an API key that can be obtained form http://developer.tomtom.com When the
 *     key is passed on the map's initialization, it is set globally for all the services. It can be overwritten with
 *     the tomtom.key method globally or per service  with tomtom.&lt;service_name&gt;.key functions. It is also
 *     possible to pass a specific key while working with instances of the services.
 * @param {String} [options.style] The base map style to be used. Should be set to one of the following values:
 *     &lt;'main', 'night'&gt;. By default, the _main_ style is used.
 * @param {String} [options.layer] The base map's layer to be used. Should be set to one of the following values:
 *     &lt;'basic', 'hybrid', 'labels'&gt;. By default, the _basic_ layer is used.
 */
define('display/layers/tileLayer',["leaflet", "security", "http"], function (L, security, http) {
    "use strict";

    var mapStyles = ["main", "night"],
        mapLayers = ["basic", "hybrid", "labels"];

    function getValueOrDefault(defaultValue, arrayToSearchIn, valueToSearchFor) {
        if (typeof valueToSearchFor === "string") {
            var index = arrayToSearchIn.indexOf(valueToSearchFor.toLowerCase());
            return index > -1 ? arrayToSearchIn[index] : defaultValue;
        }
        return defaultValue;
    }

    function use512Tiles(detectRetina, style, layer) {
        return detectRetina && L.Browser.retina && style === "main" && ["basic", "labels"].indexOf(layer) > -1;
    }

    function tiles512Options(options) {
        options.tileSize = use512Tiles(options.detectRetina, options.style, options.layer) ? 512 : 256;
        if (options.tileSize === 512) {
            options.zoomOffset -= 1;
            options.minZoom = options.minZoom > 0 ? options.minZoom - 1 : 0;
        }
    }

    function loadAnimation() {
        var opacity = "opacity",
            back = this._bgBuffer;
        back.style[L.DomUtil.TRANSITION] = opacity + " 10";
        back.style[opacity] = 0.1;
        this._tileContainer.style[opacity] = this.options.opacity;
    }

    function handleSubdomains(options) {
        if (typeof options.subdomains === "string") {
            options.subdomains = options.subdomains.split("");
        }
    }

    L.TomTomLayer = L.TileLayer.extend({
        options: {
            attribution: "",
            minZoom: 2,
            maxZoom: 18,
            subdomains: "abcd",
            reuseTiles: true,
            detectRetina: false
        },
        initialize: function (options) {
            options = L.setOptions(this, options);
            if (options.bounds) {
                options.bounds = L.latLngBounds(options.bounds);
            }
            security.setKey(options.key);
            var key = security.getKey("services/display");
            options.accessToken = key;
            /* TODO: if 512 tiles are working uncomment the following line
             this._url = http.protocol() + "://{s}.api.tomtom.com/map/1/tile/{layer}/{style}/{z}/{x}/{y}.png?tileSize={tileSize}" +(key ? "&key={accessToken}" : "");
             */
            this._url = http.protocol() + "://{s}.api.tomtom.com/map/1/tile/{layer}/{style}/{z}/{x}/{y}.png" + (key ? "?key={accessToken}" : "");
            options.style = getValueOrDefault("main", mapStyles, options.style);
            options.layer = getValueOrDefault("basic", mapLayers, options.layer);
            handleSubdomains(options);
            tiles512Options(options);
        },
        onAdd: function (map) {
            L.TileLayer.prototype.onAdd.call(this, map);
            if (this._animated) {
                this.on("load", loadAnimation);
            }
            this._updateOpacity();
        }
    });

    L.tomTomLayer = function (options) {
        return new L.TomTomLayer(options);
    };

    return L.tomTomLayer;
});
